import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, ChevronUp, CornerDownRight, GripHorizontal, X } from 'lucide-react';
import OsScroll from './ui/OsScroll';
import { KvTable, KvRow } from './ui/KvTable';
import CompactSelectPicker from './ui/CompactSelectPicker';
import Tooltip from './ui/Tooltip';
import SyntaxHighlightInput from './ui/SyntaxHighlightInput';
import { extractVariableDependencies } from '../utils/resolveVariables';
import { isDynamicVariable, DYNAMIC_VARIABLE_DOCS } from '../utils/dynamicVariables';
import { readOptions, shouldOfferPicker } from '../utils/variableOptions';
import VariableValuePicker from './ui/VariableValuePicker';
import VariableEditorPopup from './ui/VariableEditorPopup';
import { displayValueOf } from '../utils/variableDisplay';
import { openFolderVariablesFor } from '../utils/openFolderVariables';

/**
 * Every variable THIS request uses, with its value and where it came from.
 *
 * The question it answers is one the app could not answer before: not "what
 * variables exist" — the environment editor lists those — but "what is this
 * request actually going to send". Answering it meant opening the environment
 * editor, reading the URL, the headers and the auth, and matching them up by
 * eye, which is exactly the kind of work a tool should not leave to a person.
 *
 * Floating and minimisable rather than a tab, because it is a companion to
 * editing rather than a place you go: you want it open WHILE changing a URL,
 * and a tab would replace the thing you are looking at.
 *
 * Scoped to the request rather than showing everything — a long environment
 * list is what made this hard to read in the first place.
 */

// Two-column rows read fine narrower than the stacked layout needed; the panel
// is a companion to the editor and 360 was claiming more of it than it earned.
const PANEL_WIDTH = 320;
const MARGIN = 16;

/**
 * The scope, as one letter.
 *
 * It used to spell COLLECTION out, and between that and the VIA chip a row had
 * nothing left for the thing you actually came to read — the variable's NAME,
 * which truncated to a character or two (owner, 23.09.2026, with a screenshot
 * of a row reading `1`). The word is the least surprising part of the row: you
 * are looking for a name, and the scope is what you check second. A letter with
 * the word on hover keeps both and spends 14px instead of 70.
 */
const SCOPE_TAGS = {
  folder:      { letter: 'F', hint: 'Folder variable' },
  collection:  { letter: 'C', hint: 'Collection variable' },
  environment: { letter: 'E', hint: 'Environment variable' },
  workspace:   { letter: 'W', hint: 'Workspace variable' },
  global:      { letter: 'G', hint: 'Global variable' },
  dynamic:     { letter: '$', hint: 'Generated at request time' },
  // Not a scope — the absence of one, and the reason a request quietly sends
  // `{{name}}` as literal text. It keeps its red and gets the loudest glyph.
  missing:     { letter: '!', hint: 'Not defined in any scope' },
};

function ScopeTag({ scope }) {
  const tag = SCOPE_TAGS[scope] || { letter: '?', hint: scope };
  return (
    <Tooltip content={tag.hint} side="bottom-end">
      <span className={`rvp-scope rvp-scope--${scope}`} aria-label={tag.hint}>
        {tag.letter}
      </span>
    </Tooltip>
  );
}

export default function RequestVariablesPanel({
  open,
  onClose,
  requestFields,
  resolveScope,
  environments,
  selectedEnvironment,
  onSelectEnvironment,
  onVariableUpdate,
  variableContext,
  workspaceVariables,
  globalVariables,
  collectionVariables,
  folderVariables,
}) {
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState(null);
  const [draft, setDraft] = useState({});
  const dragRef = useRef(null);

  // Names used by the request as it stands. Recomputed from the live fields,
  // so adding `{{token}}` to a header makes it appear here without a refresh.
  // Names the request mentions AND the ones only reachable through another
  // variable's value: `{{baseUrl}}` where baseUrl is `example.com/{{hotelid}}`
  // depends on hotelid, and an undefined hotelid ships as literal text in the
  // URL — invisible until the request 404s.
  const deps = useMemo(
    () => (open ? extractVariableDependencies(requestFields, (n) => resolveScope?.(n)?.value) : []),
    [open, requestFields, resolveScope]
  );

  const rows = useMemo(
    () =>
      deps.map(({ name, via }) => {
        if (isDynamicVariable(name)) {
          return {
            name,
            via,
            scope: 'dynamic',
            value: DYNAMIC_VARIABLE_DOCS[name] || 'Computed at request time',
            editable: false,
          };
        }
        const found = resolveScope?.(name);
        if (found?.scope === 'folder') {
          // Not inline-editable: the inline writer has no folder scope, and a
          // write landing in collection would stay shadowed by the folder (D2).
          // Clicking opens the Folder variables modal instead.
          return {
            name,
            via,
            scope: 'folder',
            value: displayValueOf(found),
            folderId: found.folderId,
            folderName: found.folderName,
            collectionId: found.collectionId,
            editable: false,
          };
        }
        return {
          name,
          options: readOptions(found),
          // Which variable pulled this one in — null when the request names it
          // directly. Shown so `hotelid` is not a mystery row.
          via,
          scope: found?.scope ?? 'missing',
          value: found?.value ?? '',
          editable: !!found?.scope && found.scope !== 'dynamic',
        };
      }),
    [deps, resolveScope]
  );

  // Default position: bottom-right of the viewport, out of the way of the URL
  // bar and the tab strip, both of which live at the top.
  useEffect(() => {
    if (!open || pos) return;
    setPos({
      left: Math.max(MARGIN, window.innerWidth - PANEL_WIDTH - MARGIN),
      top: Math.max(MARGIN, window.innerHeight - 380),
    });
  }, [open, pos]);

  const onPointerDown = useCallback(
    (e) => {
      // Same pointer-drag idiom the resizable splitter uses; there is no
      // floating-window component in the app to reuse.
      if (e.button !== 0 || !pos) return;
      // Not on the buttons. setPointerCapture below redirects every subsequent
      // pointer event to the header, so a press that began on minimise or
      // close never became a click on them — the panel could be dragged but
      // not closed.
      if (e.target?.closest?.('button')) return;
      dragRef.current = { dx: e.clientX - pos.left, dy: e.clientY - pos.top };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [pos]
  );

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    // Clamped so the panel can never be dragged somewhere it cannot be dragged
    // back from — a header off-screen is a panel you have to reload to lose.
    const left = Math.min(
      Math.max(0, e.clientX - dragRef.current.dx),
      Math.max(0, window.innerWidth - 80)
    );
    const top = Math.min(
      Math.max(0, e.clientY - dragRef.current.dy),
      Math.max(0, window.innerHeight - 40)
    );
    setPos({ left, top });
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current = null;
  }, []);

  /**
   * "Edit options…" in a value's dropdown.
   *
   * The same editor every other surface opens, rather than a second way to edit
   * a variable that would drift from it. It is positioned at the row, so the
   * list you were just looking at is what it appears over.
   */
  const [optionsEditor, setOptionsEditor] = useState(null);

  const openOptionsEditor = (row) => {
    const anchor = rowRefs.current[row.name]?.getBoundingClientRect();
    setOptionsEditor({
      name: row.name,
      value: draft[row.name] ?? row.value,
      type: row.scope,
      exists: true,
      options: row.options,
      x: anchor ? anchor.left : MARGIN,
      y: anchor ? anchor.bottom : MARGIN,
      row,
    });
  };

  /** A folder row is edited in the Folder variables modal (D2). */
  const openFolderVariables = (row) => {
    openFolderVariablesFor({ type: 'folder', folderId: row?.folderId, collectionId: row?.collectionId }, variableContext);
  };

  /** The rows' DOM nodes, so the editor opens where the row is. */
  const rowRefs = useRef({});

  /**
   * Pending debounce timers, keyed by variable name, plus the last row seen for
   * each — a flush on unmount has to know the scope it is committing into, and
   * by then the render that produced the row is gone.
   */
  const saveTimers = useRef({});
  const pendingRows = useRef({});

  /**
   * Save AFTER TYPING STOPS, not only on blur.
   *
   * The value fed the URL preview and the request itself, so until focus left
   * the field the app was still resolving the old value — you would hover a URL
   * and read something you had already changed. Blur is not the moment the
   * user's intent is complete; a pause is.
   */
  const AUTOSAVE_MS = 500;

  const scheduleSave = (row, value) => {
    pendingRows.current[row.name] = row;
    clearTimeout(saveTimers.current[row.name]);
    saveTimers.current[row.name] = setTimeout(() => {
      delete saveTimers.current[row.name];
      commitValue(row, value);
    }, AUTOSAVE_MS);
  };

  // Anything still pending when the panel closes is committed, not dropped.
  // A debounce whose timer dies with the component silently loses the last
  // edit — exactly the shape of the AI-chat bug from 22.08.2026.
  useEffect(
    () => () => {
      Object.values(saveTimers.current).forEach(clearTimeout);
      saveTimers.current = {};
    },
    []
  );

  const commitValue = (row, next) => {
    if (next === undefined || next === row.value) return;
    onVariableUpdate?.(
      // `options` is carried through explicitly: both commit paths rebuild the
      // variable from scratch, so anything not named here is dropped.
      { key: row.name, value: next, enabled: true, ...(row.options?.length ? { options: row.options } : {}) },
      { scope: row.scope, ...(variableContext || {}) }
    );
    setDraft((d) => {
      const copy = { ...d };
      delete copy[row.name];
      return copy;
    });
  };

  const commit = (row) => {
    clearTimeout(saveTimers.current[row.name]);
    delete saveTimers.current[row.name];
    const next = draft[row.name];
    if (next === undefined || next === row.value) return;
    onVariableUpdate?.(
      // `options` is carried through explicitly: both commit paths rebuild the
      // variable from scratch, so anything not named here is dropped.
      { key: row.name, value: next, enabled: true, ...(row.options?.length ? { options: row.options } : {}) },
      { scope: row.scope, ...(variableContext || {}) }
    );
    setDraft((d) => {
      const copy = { ...d };
      delete copy[row.name];
      return copy;
    });
  };

  if (!open || !pos) return null;

  return ReactDOM.createPortal(
    <div
      className="rvp"
      style={{ left: pos.left, top: pos.top, width: PANEL_WIDTH }}
      role="dialog"
      aria-label="Variables in this request"
    >
      <div
        className="rvp-head"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <GripHorizontal size={14} aria-hidden className="rvp-grip" />
        <span className="rvp-title">
          Variables <span className="rvp-count">{rows.length}</span>
        </span>
        <button
          type="button"
          className="rvp-icon-btn"
          onClick={() => setMinimized((m) => !m)}
          title={minimized ? 'Expand' : 'Minimize'}
          aria-label={minimized ? 'Expand variables panel' : 'Minimize variables panel'}
        >
          {minimized ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <button
          type="button"
          className="rvp-icon-btn"
          onClick={onClose}
          title="Close"
          aria-label="Close variables panel"
        >
          <X size={13} />
        </button>
      </div>

      {/* OsScroll, not `overflow-y: auto`: macOS WKWebView reverts a styled
          ::-webkit-scrollbar to the wide native bar the moment the pointer
          touches it, which on a 360px floating panel eats the values. */}
      {!minimized && (
        <OsScroll className="rvp-body" defer>
          {/* The app's own picker, not a native <select>: the browser's menu is
              OS-chrome that ignores the theme, and inside a floating dark panel
              it was the one element that looked bolted on. CompactSelectPicker
              portals its menu with fixed positioning, so the panel's own
              stacking and overflow cannot clip it.

              Not a <label> wrapper any more — the picker is a button, and a
              label pointing at a button is meaningless to a screen reader. The
              caption is plain text and the picker carries its own aria-label. */}
          {/* No "Environment" caption beside it. The picker's own value says
              what it is — "No environment", or the environment's name — so the
              word was a label for something already labelled, in a 320px panel
              where every pixel it took came off the row below (owner,
              23.09.2026). The accessible name stays on the picker. */}
          <div className="rvp-env">
            <CompactSelectPicker
              className="rvp-env-select"
              menuClassName="rvp-env-menu"
              aria-label="Environment"
              menuMinWidth={200}
              value={selectedEnvironment?.id != null ? String(selectedEnvironment.id) : ''}
              options={[
                { value: '', label: 'No environment' },
                ...(environments || []).map((env) => ({
                  value: String(env.id),
                  label: env.name,
                })),
              ]}
              onChange={(next) => {
                const env = (environments || []).find((x) => String(x.id) === next) || null;
                onSelectEnvironment?.(env);
              }}
            />
          </div>

          {rows.length === 0 && (
            <p className="rvp-empty">This request uses no variables.</p>
          )}

          <KvTable className="rvp-table">
            {rows.map((row) => (
              <KvRow
                key={row.name}
                bare
                renderKey={
                  <div
                    className="rvp-key-cell"
                    ref={(node) => { rowRefs.current[row.name] = node; }}
                  >
                    {/* Hard left, in a gutter of its own. Scanning a column of
                        letters works; hunting for one that moves with the length
                        of each name does not (owner, 23.09.2026). */}
                    <ScopeTag scope={row.scope} />
                    {/* A variable the request never names itself is indented under
                        the one that pulled it in — the schema editor's idiom, for
                        the same reason: belonging reads in the left edge faster
                        than it reads in a chip. The arrow IS the indent, and it
                        replaces "VIA BASEURL", whose spelled-out parent name was
                        the widest thing in the row and is read once. */}
                    {row.via && (
                      <Tooltip content={`Used inside {{${row.via}}}`} side="bottom-start">
                        <span className="rvp-via" aria-label={`Used inside ${row.via}`}>
                          <CornerDownRight size={10} aria-hidden />
                        </span>
                      </Tooltip>
                    )}
                    {/* The name takes every pixel the chips beside it do not, and
                        carries its own hint rather than truncating into silence —
                        a row that read `1` is what started this. */}
                    <Tooltip content={row.name} side="bottom-start">
                      <span className="rvp-name">{row.name}</span>
                    </Tooltip>
                  </div>
                }
                renderValue={
                  // A variable that remembers alternatives gets the app's own
                  // dropdown rather than a free-text field: picking swaps the
                  // ACTIVE value inline, the alternatives stay, and resolution
                  // never sees anything but a plain string.
                  row.editable && shouldOfferPicker(row) ? (
                    <VariableValuePicker
                      variable={row}
                      value={draft[row.name] ?? row.value}
                      className="rvp-value rvp-value--options"
                      menuClassName="rvp-option-menu"
                      ariaLabel={`Value of ${row.name}`}
                      onChange={(next) => {
                        setDraft((d) => ({ ...d, [row.name]: next }));
                        commitValue(row, next);
                      }}
                      /* The menu offers the alternatives and no way to change
                         them, so removing one meant finding the variable in some
                         other table — the table this panel exists to save you
                         opening (owner, 23.09.2026). Opens the ordinary variable
                         editor, over the row, which is where options are added
                         and removed everywhere else. */
                      onEditOptions={() => openOptionsEditor(row)}
                    />
                  ) : row.editable ? (
                    /* ALWAYS the highlighter, never a plain input that is
                       swapped for one.

                       It used to render an <input> and switch to this the moment
                       the value contained `{{`, to keep a contenteditable out of
                       rows that did not need one. But the switch happens ON THE
                       KEYSTROKE that completes `{{`: React unmounts the input
                       mid-word, the caret goes with it, and you have to click
                       back in to keep typing — which is precisely what you are
                       doing when you type `{{` here (owner, 23.09.2026).

                       A field that changes identity while you type it cannot be
                       fixed by making the swap smoother; there must be no swap. */
                    <SyntaxHighlightInput
                      className="rvp-value"
                      value={draft[row.name] ?? row.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDraft((d) => ({ ...d, [row.name]: value }));
                        scheduleSave(row, value);
                      }}
                      onBlur={() => commit(row)}
                      onKeyDown={(e) => {
                        // The field swallows Enter itself (single-line); this is
                        // the parent's chance to treat it as "I am done".
                        if (e.key === 'Enter') commit(row);
                      }}
                      selectedEnvironment={selectedEnvironment}
                      workspaceVariables={workspaceVariables}
                      globalVariables={globalVariables}
                      collectionVariables={collectionVariables}
                      folderVariables={folderVariables}
                      variableContext={variableContext}
                      /* Clicking a NESTED `{{…}}` inside this value opens the
                         variable editor, and without this the editor had no way
                         to write: the field falls back to an
                         `updateEnvironmentVariable` event addressed to the
                         SELECTED environment, so with "No environment" chosen —
                         the panel's own default — the save went nowhere and the
                         chip stayed red (owner, 23.09.2026). */
                      onVariableUpdate={onVariableUpdate}
                      aria-label={`Value of ${row.name}`}
                    />
                  ) : row.scope === 'folder' ? (
                    <span
                      role="button"
                      tabIndex={0}
                      className="rvp-value rvp-value--readonly rvp-value--folder cursor-pointer"
                      title={row.folderName ? `${row.folderName}: ${row.value}` : row.value}
                      aria-label={`Edit folder variable ${row.name}`}
                      onClick={() => openFolderVariables(row)}
                      onKeyDown={(e) => { if (e.key === 'Enter') openFolderVariables(row); }}
                    >
                      {row.value}
                    </span>
                  ) : (
                    <span
                      className={`rvp-value rvp-value--readonly rvp-value--${row.scope}`}
                      title={row.scope === 'missing' ? undefined : row.value}
                    >
                      {/* A missing variable shows an EMPTY value, not a sentence
                          explaining itself. The MISSING chip beside it already
                          says so in one word, and the prose was long enough to
                          truncate — so the cell spent its width telling you it
                          had nothing to tell you. */}
                      {row.scope === 'missing' ? '' : row.value}
                    </span>
                  )
                }
              />
            ))}
          </KvTable>
        </OsScroll>
      )}

      {/* Rendered inside the panel's portal, so it inherits nothing from the
          request behind it. Saving goes through the same `onVariableUpdate` as
          every other edit here — the popup is the UI, not a second writer. */}
      {optionsEditor && (
        <VariableEditorPopup
          key={optionsEditor.name}
          editingVariable={optionsEditor}
          variableContext={variableContext}
          selectedEnvironment={selectedEnvironment}
          workspaceVariables={workspaceVariables}
          globalVariables={globalVariables}
          collectionVariables={collectionVariables}
          folderVariables={folderVariables}
          onVariableUpdate={onVariableUpdate}
          onSave={(value, scope, name, options) => {
            onVariableUpdate?.(
              { key: name || optionsEditor.name, value, enabled: true, ...(options?.length ? { options } : {}) },
              { scope: scope || optionsEditor.type, ...(variableContext || {}) }
            );
            setDraft((d) => {
              const copy = { ...d };
              delete copy[optionsEditor.name];
              return copy;
            });
            setOptionsEditor(null);
          }}
          onClose={() => setOptionsEditor(null)}
        />
      )}
    </div>,
    document.body
  );
}
